import type { Resources, EraId } from '@/models/types';
import { formatNumber } from '@/utils/format';
import './styles.css';

const RESOURCES_CONFIG = [
  { key: 'gold', icon: '🪙', label: 'Gold' },
  { key: 'food', icon: '🌾', label: 'Food' },
  { key: 'faith', icon: '🔮', label: 'Faith' },
  { key: 'knowledge', icon: '📜', label: 'Knowledge' },
] as const;

const ERA_NAMES: Record<string, string> = {
  ancient: '⚱️ Ancient Era',
  medieval: '🏰 Medieval Era',
  modern: '🔭 Renaissance',
  industrial: '⚙️ Industrial',
  contemporary: '🏙️ Modern',
  future: '🚀 Future',
};

export class HUD {
  private container: HTMLElement;
  private valueEls: Record<string, HTMLElement> = {};
  private eraEl: HTMLElement;
  private gemsEl: HTMLElement;
  private productionEl: HTMLElement;
  private buffEl: HTMLElement;

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.id = 'hud';
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%;
      display: flex; flex-wrap: wrap; align-items: center;
    `;

    // Era
    this.eraEl = document.createElement('div');
    this.eraEl.className = 'hud-era';
    this.eraEl.style.flexBasis = '100%';
    this.container.appendChild(this.eraEl);

    // Resources row
    const resRow = document.createElement('div');
    resRow.style.cssText = 'display:flex; width:100%; justify-content:space-around; padding:4px 0;';

    for (const res of RESOURCES_CONFIG) {
      const el = document.createElement('div');
      el.className = 'hud-resource';
      el.innerHTML = `
        <span class="hud-resource-icon">${res.icon}</span>
        <span class="hud-resource-value" id="hud-${res.key}">0</span>
        <span class="hud-resource-label">${res.label}</span>
      `;
      resRow.appendChild(el);
      this.valueEls[res.key] = el.querySelector(`#hud-${res.key}`)!;
    }
    this.container.appendChild(resRow);

    // Production + gems row
    const bottomRow = document.createElement('div');
    bottomRow.style.cssText = 'display:flex; width:100%; justify-content:space-between; padding:2px 8px;';

    this.productionEl = document.createElement('div');
    this.productionEl.className = 'hud-production';

    this.buffEl = document.createElement('div');
    this.buffEl.className = 'hud-buff';

    this.gemsEl = document.createElement('div');
    this.gemsEl.className = 'hud-gems';

    bottomRow.appendChild(this.productionEl);
    bottomRow.appendChild(this.buffEl);
    bottomRow.appendChild(this.gemsEl);
    this.container.appendChild(bottomRow);

    parent.appendChild(this.container);
  }

  update(resources: Resources, gems: number, era: EraId, productionRate: number, activeEventName?: string, nameColor?: string, badge?: string): void {
    for (const res of RESOURCES_CONFIG) {
      const el = this.valueEls[res.key]!;
      const newVal = formatNumber(resources[res.key as keyof Resources]);
      if (el.textContent !== newVal) {
        el.textContent = newVal;
        el.classList.remove('number-pop');
        void el.offsetWidth;
        el.classList.add('number-pop');
      }
    }
    this.gemsEl.textContent = `💎 ${gems}`;
    const badgeStr = badge ? `${badge} ` : '';
    this.eraEl.textContent = `${badgeStr}${ERA_NAMES[era] ?? era}`;
    if (nameColor && nameColor !== '#c9a44c') {
      this.eraEl.style.color = nameColor;
    } else {
      this.eraEl.style.color = '';
    }
    this.productionEl.textContent = productionRate > 0 ? `⚡ +${formatNumber(productionRate)}/s` : '⚡ idle';
    this.buffEl.textContent = activeEventName ?? '';
    this.buffEl.style.display = activeEventName ? 'block' : 'none';
  }

  destroy(): void {
    this.container.remove();
  }
}
